/**
 * usePushNotifications — manages browser push subscription lifecycle.
 *
 * Returns:
 *   { supported, status: "idle"|"prompt"|"granted"|"denied"|"unsupported"|"loading",
 *     subscribed, enable(), disable(), sendTest() }
 *
 * Usage in /profil settings:
 *   const { status, subscribed, enable, disable, sendTest } = usePushNotifications();
 */
import { useCallback, useEffect, useState } from "react";
import api from "./api";

const VAPID_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || "";

function urlBase64ToUint8Array(base64) {
    const padding = "=".repeat((4 - (base64.length % 4)) % 4);
    const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(b64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
    return arr;
}

const supported =
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;

export default function usePushNotifications() {
    const [status, setStatus] = useState(supported ? "loading" : "unsupported");
    const [subscribed, setSubscribed] = useState(false);

    const refresh = useCallback(async () => {
        if (!supported) return setStatus("unsupported");
        const perm = Notification.permission;
        if (perm === "denied") {
            setStatus("denied");
            setSubscribed(false);
            return;
        }
        try {
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.getSubscription();
            setSubscribed(!!sub);
            setStatus(perm === "granted" ? "granted" : "prompt");
        } catch (e) {
            setStatus("idle");
        }
    }, []);

    useEffect(() => {
        if (!supported) return;
        // Register service worker once
        navigator.serviceWorker
            .register("/sw.js")
            .then(() => refresh())
            .catch((e) => {
                console.error("SW register failed", e);
                setStatus("idle");
            });
    }, [refresh]);

    const enable = useCallback(async () => {
        if (!supported) {
            return { ok: false, reason: "unsupported" };
        }
        if (!VAPID_KEY) {
            console.error("[push] VAPID public key missing in .env (REACT_APP_VAPID_PUBLIC_KEY)");
            return { ok: false, reason: "no_vapid_key" };
        }
        try {
            // 1) Service worker — register if not already
            let reg;
            try {
                reg = await navigator.serviceWorker.register("/sw.js");
            } catch (e) {
                console.error("[push] SW register failed:", e);
                return { ok: false, reason: "sw_register_failed", error: e?.message };
            }
            // Wait for it to be ready (active)
            reg = await navigator.serviceWorker.ready;

            // 2) Permission
            const perm = await Notification.requestPermission();
            if (perm !== "granted") {
                setStatus(perm);
                return { ok: false, reason: "permission_" + perm };
            }

            // 3) Subscribe (if not already)
            let sub = await reg.pushManager.getSubscription();
            if (!sub) {
                try {
                    sub = await reg.pushManager.subscribe({
                        userVisibleOnly: true,
                        applicationServerKey: urlBase64ToUint8Array(VAPID_KEY),
                    });
                } catch (e) {
                    console.error("[push] subscribe() failed:", e);
                    // Brave / private mode / Firefox sometimes block this
                    return {
                        ok: false,
                        reason: "subscribe_failed",
                        error: e?.message,
                    };
                }
            }

            // 4) Persist server-side
            try {
                await api.post("/push/subscribe", {
                    endpoint: sub.endpoint,
                    keys: {
                        p256dh: btoa(
                            String.fromCharCode(...new Uint8Array(sub.getKey("p256dh"))),
                        ),
                        auth: btoa(
                            String.fromCharCode(...new Uint8Array(sub.getKey("auth"))),
                        ),
                    },
                    expirationTime: sub.expirationTime || null,
                });
            } catch (e) {
                console.error("[push] server persist failed:", e);
                return { ok: false, reason: "persist_failed", error: e?.message };
            }

            setStatus("granted");
            setSubscribed(true);
            return { ok: true };
        } catch (e) {
            console.error("[push] enable() unexpected error:", e);
            return { ok: false, reason: "unexpected", error: e?.message };
        }
    }, []);

    const disable = useCallback(async () => {
        if (!supported) return;
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
            try {
                await api.post("/push/unsubscribe", {
                    endpoint: sub.endpoint,
                    keys: { p256dh: "", auth: "" },
                });
            } catch (e) {
                console.warn("server unsubscribe failed", e);
            }
            await sub.unsubscribe();
        }
        setSubscribed(false);
    }, []);

    const sendTest = useCallback(async () => {
        try {
            await api.post("/push/test");
            return true;
        } catch (e) {
            return false;
        }
    }, []);

    return { supported, status, subscribed, enable, disable, sendTest, refresh };
}
